'use client';

import { use, useLayoutEffect } from 'react';
import { FormActionStateContext, FormManifestContext } from '../Form/FormBuilder';
import { MEETING_ASIDE_KEY, SUCCESS_STEP_KEY, StepsContext } from '../Form/StepsProvider';

/**
 * Coordinates between the form action state and the step URL:
 * - On `actionState.result === 'success'`, routes the dialog to the success
 *   pseudo-step (URL becomes `?s=success`), except when the user is on the
 *   meeting aside (`?s=book-meeting`) so “Book a meeting” from success works.
 * - On mount with a stale `?s=success` URL but no successful submission,
 *   redirects back to the first step so the user lands on a usable state.
 *
 * The redirect uses `useLayoutEffect` so it runs synchronously after the
 * commit but before the browser paints. That avoids a flash of the success
 * panel when the URL is stale, and a flash of the submit step when an actual
 * successful submission lands.
 *
 * Renders nothing — it is purely a side effect. Lives inside
 * `SteppedFormContent` so it has access to both `FormActionStateContext` and
 * `StepsContext`.
 */
export function SuccessNavigator() {
  const {
    actionState
  } = use(FormActionStateContext);
  const manifest = use(FormManifestContext);
  const ctx = use(StepsContext);
  const goToStep = ctx?.goToStep;
  const activeStep = ctx?.activeStep;
  const result = actionState.result;
  useLayoutEffect(() => {
    if (!goToStep) return;
    if (result === 'success') {
      if (activeStep !== SUCCESS_STEP_KEY && activeStep !== MEETING_ASIDE_KEY) {
        goToStep(SUCCESS_STEP_KEY);
      }
      return;
    }
    if (activeStep === SUCCESS_STEP_KEY) {
      const firstStepKey = manifest.steps[0]?._key;
      if (firstStepKey) {
        goToStep(firstStepKey);
      }
    }
  }, [result, goToStep, activeStep, manifest.steps]);
  return null;
}